/*
 * Story cards page, on the Ryze structure.
 *
 * One card shows at a time. Only buttons move between cards: the next
 * button goes forward, the back arrow goes back, and Skip jumps to the last
 * card. Tapping or swiping the card itself does nothing, as on Ryze. The
 * review quote box on the reviews card has its own prev/next buttons and
 * swipe, as Ryze's review row does.
 */
const SWIPE_MIN = 50;

class StoryCards {
  constructor(root) {
    this.root = root;
    this.cards = [...root.querySelectorAll('[data-sc-card]')];
    this.bars = [...root.querySelectorAll('[data-sc-bar]')];
    this.back = root.querySelector('[data-sc-back]');
    this.skip = root.querySelector('[data-sc-skip]');
    this.track = root.querySelector('[data-sc-track]');
    this.index = 0;
    if (!this.cards.length) return;

    this.bind();
    root.classList.add('is-ready');
    this.render();
  }

  go(i) {
    const next = Math.max(0, Math.min(this.cards.length - 1, i));
    if (next === this.index) return;
    this.index = next;
    this.render();
  }

  render() {
    this.cards.forEach((card, i) => {
      const active = i === this.index;
      card.classList.toggle('is-active', active);
      card.classList.toggle('is-before', i < this.index);
      card.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        card.removeAttribute('inert');
        card.scrollTop = 0;
      } else {
        card.setAttribute('inert', '');
      }
    });
    this.playConfetti(this.cards[this.index]);
    this.bars.forEach((bar, i) => {
      bar.classList.toggle('is-done', i < this.index);
      bar.classList.toggle('is-current', i === this.index);
    });
    if (this.back) this.back.disabled = this.index === 0;
    if (this.skip) this.skip.hidden = this.index === this.cards.length - 1;
  }

  // A short confetti drop each time the offer card shows. Skipped for reduced motion.
  playConfetti(card) {
    const box = card && card.querySelector('[data-sc-confetti]');
    if (!box) return;
    // Clear any pieces from an earlier play so the drop restarts cleanly
    clearTimeout(this.confettiTimer);
    box.replaceChildren();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const colours = ['#f4c430', '#e85d75', '#5cb85c', '#4a90d9', '#ffffff'];
    for (let i = 0; i < 36; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'story-cards__confetti-piece' + (i % 3 === 0 ? ' is-round' : '');
      piece.style.left = Math.random() * 100 + '%';
      piece.style.background = colours[i % colours.length];
      piece.style.animationDelay = Math.random() * 0.6 + 's';
      piece.style.animationDuration = 2.2 + Math.random() * 1.6 + 's';
      piece.style.setProperty('--drift', Math.round(Math.random() * 120 - 60) + 'px');
      piece.style.setProperty('--spin', Math.round(Math.random() * 720 - 360) + 'deg');
      box.appendChild(piece);
    }
    this.confettiTimer = setTimeout(() => box.replaceChildren(), 5000);
  }

  bind() {
    this.root.addEventListener('click', (e) => {
      if (e.target.closest('[data-sc-next]')) {
        this.go(this.index + 1);
        return;
      }
      if (e.target.closest('[data-sc-back]')) {
        this.go(this.index - 1);
        return;
      }
      if (e.target.closest('[data-sc-skip]')) {
        this.go(this.cards.length - 1);
        return;
      }
    });

    // Theme editor: show the card being edited
    document.addEventListener('shopify:block:select', (e) => {
      const i = this.cards.indexOf(e.target);
      if (i > -1) this.go(i);
    });

    // Review quote carousels
    this.root.querySelectorAll('[data-sc-quotes]').forEach((box) => this.bindQuotes(box));
  }

  bindQuotes(box) {
    const quotes = [...box.querySelectorAll('[data-sc-quote]')];
    const dots = [...box.querySelectorAll('[data-sc-quote-dot]')];
    let current = 0;

    const show = (n) => {
      current = (n + quotes.length) % quotes.length;
      quotes.forEach((el, i) => el.classList.toggle('is-active', i === current));
      dots.forEach((el, i) => el.classList.toggle('is-active', i === current));
    };

    box.querySelector('[data-sc-quote-prev]')?.addEventListener('click', () => show(current - 1));
    box.querySelector('[data-sc-quote-next]')?.addEventListener('click', () => show(current + 1));

    let startX = null;
    box.addEventListener('pointerdown', (e) => {
      if (e.target.closest('button')) return;
      startX = e.clientX;
    });
    box.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      if (Math.abs(dx) >= SWIPE_MIN && quotes.length > 1) show(current + (dx < 0 ? 1 : -1));
    });
  }
}

const init = () => {
  document.querySelectorAll('[data-story-cards]').forEach((el) => {
    if (!el.__storyCards) el.__storyCards = new StoryCards(el);
  });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);
